/* Wallpaper layout editor. Commerce is handled separately from preview geometry. */
(() => {
  'use strict';
  if (window.MarselaCustomizer) return;
  const MATERIALS = {
    'non-woven-smooth': {name: 'Traditional Smooth', rate: 30},
    'non-woven-textured': {name: 'Traditional Textured', rate: 30},
    'peel-stick-smooth': {name: 'Peel & Stick Smooth', rate: 34},
    'peel-stick-fabric': {name: 'Peel & Stick Fabric', rate: 38}
  };
  // Europe market (EUR carts): whole-euro rates, in sync with commerce.js and the quote service.
  const EUR_RATES = {'non-woven-smooth': 32, 'non-woven-textured': 32, 'peel-stick-smooth': 36, 'peel-stick-fabric': 41};
  const CURRENCY = document.body?.dataset.marselaEurEnabled === 'true' && document.body?.dataset.marselaCurrency === 'EUR' ? 'EUR' : 'USD';
  const REFERENCES = {
    person: {name: 'Person', widthCm: 52, heightCm: 165, initialX: .80},
    toddler: {name: 'Toddler', widthCm: 36, heightCm: 90, initialX: .67},
    crib: {name: 'Crib', widthCm: 135, heightCm: 90, initialX: .39}
  };
  const instances = new Map();
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const rounded = (value, digits = 6) => Number(value.toFixed(digits));
  const toCm = (value, unit) => Number(value) * (unit === 'in' ? 2.54 : 1);
  const fromCm = (value, unit) => value / (unit === 'in' ? 2.54 : 1);
  const validCm = value => Number.isFinite(value) && value > 0 && value <= 1500;
  // Business limits (must match the quote service): walls up to 420 cm high, billed at least 4 m².
  const MAX_HEIGHT_CM = 420, MIN_BILLABLE_M2 = 4;
  const validH = value => validCm(value) && value <= MAX_HEIGHT_CM;
  const validFor = (key, value) => key === 'heightCm' ? validH(value) : validCm(value);
  // English defaults. layout/theme.liquid renders the store language's copy
  // (locales/*.json, js.customizer) into window.MarselaStrings.customizer.
  const STRINGS = {
    artworkUnavailable: 'Artwork preview is unavailable.', loadingArtwork: 'Loading artwork preview…',
    originalProportions: 'Original proportions', wider: '{percent}% wider', taller: '{percent}% taller',
    selectedCrop: 'Your selected wallpaper crop', confirmAdd: 'Confirm & add to cart', saveFailed: 'Your selection could not be saved. Please try again.',
    panels_one: '{count} panel × {width} {unit}', panels_other: '{count} panels × {width} {unit}',
    referencesCleared: 'All room references removed.', referenceAdded: '{name} added. Drag to move, or use the left and right arrow keys.',
    referenceRemoved: '{name} removed.', referenceMoved: '{name} position updated.',
    person: 'Person', toddler: 'Toddler', crib: 'Crib',
    addPerson: 'Add person', removePerson: 'Remove person', addToddler: 'Add toddler', removeToddler: 'Remove toddler', addCrib: 'Add crib', removeCrib: 'Remove crib',
    referenceLabel: '{name}, {height} cm tall. Drag or use left and right arrow keys to move.', zoomValue: '{value}× zoom',
    extendWidth: 'Extends the design width by up to 10%.', extendHeight: 'Extends the design height by up to 10%.',
    minimum: 'Minimum order is {area} m². Smaller walls are priced as {area} m².', pricedAs: 'priced as {area} m²', enterSize: 'Enter your wall size to see the total'
  };
  const LOCALE = (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
  const translate = key => window.MarselaStrings?.customizer?.[key] || STRINGS[key] || key;
  const pluralRules = new Intl.PluralRules(LOCALE);
  const plural = (base, count) => { const strings = window.MarselaStrings?.customizer || {}, id = `${base}_${pluralRules.select(count)}`; return strings[id] || strings[`${base}_other`] || STRINGS[id] || STRINGS[`${base}_other`]; };
  const numberFormats = new Map();
  const formatted = (value, digits = 2) => {
    const locale = LOCALE;
    const key = `${locale}:${digits}`;
    if (!numberFormats.has(key)) numberFormats.set(key, new Intl.NumberFormat(locale, {minimumFractionDigits: digits, maximumFractionDigits: digits}));
    return numberFormats.get(key).format(value);
  };

  // Stretch (owner 2026-10-25): one slider from −10 to +10 with 0 in the middle. Below 0 the
  // design stretches vertically (taller), above 0 horizontally (wider); the quote service and
  // commerce.js accept either axis. {signed} is the slider value, {axis, percent} the stored configuration.
  const signedStretch = extension => { if (Number.isFinite(extension?.signed)) return extension.signed; const percent = Number(extension?.percent) || 0; return extension?.axis === 'vertical' ? -percent : percent; };
  function designExtension(state, imageWidth, imageHeight) {
    const signed = clamp(signedStretch(state.designExtension), -10, 10);
    return {axis: signed > 0 ? 'horizontal' : signed < 0 ? 'vertical' : 'none', percent: Math.abs(signed)};
  }
  function designScale(state, imageWidth, imageHeight) {
    const extension = designExtension(state, imageWidth, imageHeight);
    const factor = 1 + extension.percent / 100;
    return {x: extension.axis === 'horizontal' ? factor : 1, y: extension.axis === 'vertical' ? factor : 1};
  }
  function cropGeometry(state, imageWidth, imageHeight) {
    if (!validCm(state.widthCm) || !validH(state.heightCm) || imageWidth <= 0 || imageHeight <= 0) return null;
    const scale = designScale(state, imageWidth, imageHeight);
    // The crop is always expressed in original, unmirrored source pixels. A
    // non-uniform preview stretch changes the source aspect, never its identity.
    const aspect = state.widthCm / state.heightCm * scale.y / scale.x;
    const zoom = clamp(state.zoom, 1, 2);
    const width = Math.min(imageWidth, imageHeight * aspect) / zoom;
    const height = width / aspect;
    const x = (imageWidth - width) * clamp(state.panX, 0, 1);
    const y = (imageHeight - height) * clamp(state.panY, 0, 1);
    return {x, y, width, height, normalized: {x: x / imageWidth, y: y / imageHeight, width: width / imageWidth, height: height / imageHeight}};
  }

  function panelPlan(widthCm) {
    const count = Math.max(1, Math.ceil(widthCm / 104));
    return {maximumPanelWidthCm: 104, count, panelWidthCm: widthCm / count, equalWidths: true};
  }

  function exampleQuote(state) {
    const material = MATERIALS[state.material], rate = CURRENCY === 'EUR' ? EUR_RATES[state.material] : material.rate;
    const area = state.widthCm * state.heightCm / 10000;
    const decimal = value => {
      const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e');
      const [whole, decimals = ''] = mantissa.split('.');
      const scale = decimals.length - Number(exponent), numerator = BigInt(whole + decimals);
      return scale >= 0 ? [numerator, 10n ** BigInt(scale)] : [numerator * 10n ** BigInt(-scale), 1n];
    };
    const [width, widthDenominator] = decimal(state.widthCm), [height, heightDenominator] = decimal(state.heightCm);
    let numerator = width * height * BigInt(rate), denominator = widthDenominator * heightDenominator * 10000n;
    const minimumApplied = width * height < BigInt(MIN_BILLABLE_M2) * 10000n * widthDenominator * heightDenominator;
    if (minimumApplied) { numerator = BigInt(MIN_BILLABLE_M2) * BigInt(rate); denominator = 1n; }
    const total = numerator * 200n < denominator ? 0 : Number((numerator + denominator - 1n) / denominator);
    return {areaM2: area, billableAreaM2: minimumApplied ? MIN_BILLABLE_M2 : area, minimumApplied, unitRate: rate, total, currency: CURRENCY};
  }

  function buildConfiguration(snapshot, identity) {
    const crop = cropGeometry(snapshot, identity.sourceWidth, identity.sourceHeight);
    if (!crop) return null;
    // Remove sub-pixel floating-point drift at source boundaries before saving.
    const normalized = Object.fromEntries(Object.entries(crop.normalized).map(([key, value]) => [key, clamp(value, 0, 1)]));
    normalized.x = Math.min(normalized.x, 1 - normalized.width);
    normalized.y = Math.min(normalized.y, 1 - normalized.height);
    return {
      schemaVersion: 1,
      productHandle: identity.productHandle,
      artwork: {reference: identity.reference, sha256: identity.sha256, asset: identity.asset, sourceWidth: identity.sourceWidth, sourceHeight: identity.sourceHeight},
      wall: {widthCm: snapshot.widthCm, heightCm: snapshot.heightCm},
      crop: normalized,
      material: snapshot.material,
      mirror: Boolean(snapshot.mirror),
      designExtension: designExtension(snapshot, identity.sourceWidth, identity.sourceHeight),
      panelPlan: panelPlan(snapshot.widthCm)
    };
  }

  // References use the same real-world scale as the wall, independent of artwork zoom.
  // Their horizontal centers are normalized within the scene interior, so resizing is stable.
  function sceneGeometry(state, width, height, padding = {}, chromeScale = 1) {
    const left = padding.left || 0, right = padding.right || 0, top = padding.top || 0, bottom = padding.bottom || 0;
    const interiorWidth = Math.max(1, width - left - right);
    const interiorHeight = Math.max(1, height - top - bottom - 62 * chromeScale);
    const scale = Math.min(interiorWidth / Math.max(state.widthCm + 110, 180), interiorHeight / Math.max(state.heightCm, 180));
    const wallWidth = state.widthCm * scale, wallHeight = state.heightCm * scale;
    const floor = height - bottom - 22 * chromeScale;
    return {width, height, left, interiorWidth, scale, floor, wallLeft: left + (interiorWidth - wallWidth) / 2, wallTop: floor - wallHeight, wallWidth, wallHeight};
  }
  function referenceGeometry(type, reference, scene) {
    const definition = REFERENCES[type];
    const width = definition.widthCm * scene.scale, height = definition.heightCm * scene.scale;
    const normalizedHalfWidth = width / scene.interiorWidth / 2;
    const x = clamp(reference.x, normalizedHalfWidth, 1 - normalizedHalfWidth);
    return {x, left: scene.left + x * scene.interiorWidth - width / 2, top: scene.floor - height, width, height};
  }

  // Preview branding only: draw after the crop/mirror operation so production
  // coordinates and artwork identity remain independent of the watermark.
  function watermarkPreview(context, width, height) {
    const fontSize = Math.max(12, width / 26);
    const gapX = fontSize * 13, gapY = fontSize * 6;
    context.save();
    context.font = `500 ${fontSize}px Georgia, serif`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = 'rgba(54, 50, 47, 0.26)';
    for (let row = 0, y = Math.min(gapY / 2, height / 2); y < height; row++, y += gapY) {
      for (let x = Math.min(gapX / 2 + (row % 2 ? gapX / 2 : 0), width / 2); x < width; x += gapX) {
        context.save(); context.translate(x, y); context.rotate(-Math.PI / 9);
        context.fillText('Marsela Wall Co', 0, 0);
        context.restore();
      }
    }
    context.restore();
  }

  function initialise(section) {
    if (instances.has(section)) return;
    const find = key => section.querySelector(`[data-ms-customizer-${key}]`);
    const canvas = find('canvas'), stage = find('stage'), width = find('width'), height = find('height'), unit = find('unit'), material = find('material');
    const zoom = find('zoom'), panX = find('pan-x'), panY = find('pan-y'), mirror = find('mirror'), reset = find('reset');
    const extend = find('extend'), extensionControls = find('extension-controls'), extensionRange = find('extension'), extensionOutput = find('extension-output'), extensionMin = find('extension-min'), extensionMax = find('extension-max');
    const wall = find('wall'), guides = find('guides'), clearReferences = find('clear-refs'), referenceStatus = find('reference-status');
    const layoutStage = find('layout-stage'), layoutArt = find('layout-art'), layoutImage = find('layout-image'), cropWindow = find('crop-window');
    const layoutView = find('layout-view'), roomView = find('room-view');
    const viewButtons = [...section.querySelectorAll('[data-ms-customizer-view]')];
    const panelLines = find('panel-lines'), layoutPanels = find('layout-panels');
    const materialOptions = [...section.querySelectorAll('[data-ms-customizer-material-option]')];
    const referenceNodes = Object.fromEntries(Object.keys(REFERENCES).map(type => [type, section.querySelector(`[data-ms-customizer-ref="${type}"]`)]));
    const referenceButtons = Object.fromEntries(Object.keys(REFERENCES).map(type => [type, section.querySelector(`[data-ms-customizer-add="${type}"]`)]));
    const error = find('error'), imageStatus = find('image-status'), saveStatus = find('save-status');
    if (![canvas, stage, width, height, unit, material, zoom, panX, panY, mirror, reset, error, imageStatus, saveStatus].every(Boolean)) return;
    const dialog = section.closest('[data-ms-customizer-dialog]');
    const entry = dialog && [...document.querySelectorAll('[data-ms-customizer-entry]')].find(form => form.dataset.customizerTarget === dialog.id);
    const entryFind = key => entry?.querySelector(`[data-ms-entry-${key}]`) || entry?.parentElement?.querySelector(`[data-ms-entry-${key}]`);
    const entryWidth = entryFind('width'), entryHeight = entryFind('height'), entryUnit = entryFind('unit'), entryError = entryFind('error');
    const entrySummary = entryFind('summary');
    const closeButton = dialog?.querySelector('[data-ms-customizer-close]');
    const confirmButton = find('confirm');
    const purchaseStatus = find('purchase-status'), verifiedPrice = find('verified-price');
    const context = canvas.getContext('2d');
    if (!context) { imageStatus.textContent = translate('artworkUnavailable'); return; }
    const controller = new AbortController();
    const options = {signal: controller.signal};
    // Designs with lettering or maps are never mirrored (the quote service rejects it too).
    const mirrorAllowed = section.dataset.mirrorAllowed !== 'false';
    const state = {widthCm: 300, heightCm: 240, unit: 'cm', material: 'non-woven-smooth', zoom: 1, panX: .5, panY: .5, mirror: false, designExtension: {axis: 'none', percent: 0}, extensionEnabled: false, references: Object.fromEntries(Object.entries(REFERENCES).map(([type, definition]) => [type, {visible: false, x: definition.initialX}]))};
    let scene = null, layoutBounds = null, layoutDrag = null, referenceDrag = null, lastReferenceAnnouncement = null;
    let activeView = layoutView ? 'layout' : 'room';
    let valid = true, imageReady = false, imageFailed = false, destroyed = false, drag = null;
    const artwork = new Image();
    artwork.crossOrigin = 'anonymous';
    artwork.decoding = 'async';
    const entryDraft = {widthCm: null, heightCm: null, unit: entryUnit?.value === 'in' ? 'in' : 'cm'};
    let confirmed = null, confirmedSelection = null, summaryDirty = true, opener = null, scrollLock = null;
    let purchasePhase = 'idle', purchaseBusy = false;
    let frame = 0, pendingFull = false, pendingCrop = false, pendingReferences = false;
    const visible = () => !dialog || dialog.open;
    const editingLocked = () => purchaseBusy || purchasePhase === 'uncertain';
    function lockPurchaseControls() {
      section.querySelectorAll('input,select,button').forEach(control => {
        if (control === confirmButton) return;
        if (editingLocked()) { if (!control.hasAttribute('data-ms-purchase-disabled')) control.dataset.msPurchaseDisabled = String(control.disabled); control.disabled = true; }
        else if (control.hasAttribute('data-ms-purchase-disabled')) { control.disabled = control.dataset.msPurchaseDisabled === 'true'; control.removeAttribute('data-ms-purchase-disabled'); }
      });
    }
    const snapshotState = () => ({...state, designExtension: designExtension(state, artwork.naturalWidth, artwork.naturalHeight), showPanelGuides: Boolean(guides?.checked), references: Object.fromEntries(Object.entries(state.references).map(([type, reference]) => [type, {...reference}]))});
    function updateEntryStep() {
      const current = dialog?.open ? '2' : confirmed && !summaryDirty ? '3' : '1';
      entry?.querySelectorAll('[data-ms-entry-step]').forEach(step => {
        if (step.dataset.msEntryStep === current) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
    }
    function dirty() {
      summaryDirty = true;
      if (entrySummary) entrySummary.hidden = true;
      if (entry) entry.dataset.selectionConfirmed = 'false';
      updateEntryStep();
      entry?.dispatchEvent(new CustomEvent('marsela:selection-invalidated', {bubbles: true}));
    }
    function lockScroll() {
      if (scrollLock) return;
      const style = document.body.style;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow'];
      scrollLock = {x: window.scrollX, y: window.scrollY, styles: Object.fromEntries(properties.map(key => [key, style[key]]))};
      Object.assign(style, {position: 'fixed', top: `${-scrollLock.y}px`, left: `${-scrollLock.x}px`, right: '0', width: '100%', overflow: 'hidden'});
    }
    function unlockScroll(restoreFocus = true) {
      if (scrollLock) {
        const previous = scrollLock;
        scrollLock = null;
        Object.assign(document.body.style, previous.styles);
        window.scrollTo({left: previous.x, top: previous.y, behavior: 'instant'});
      }
      if (restoreFocus && opener?.isConnected) opener.focus({preventScroll: true});
    }
    function schedule(kind = 'full') {
      if (destroyed) return;
      if (kind === 'full') pendingFull = true;
      else if (kind === 'crop') pendingCrop = true;
      else pendingReferences = true;
      if (!frame) frame = requestAnimationFrame(flush);
    }
    function flush() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      const full = pendingFull, crop = pendingCrop, refs = pendingReferences;
      pendingFull = pendingCrop = pendingReferences = false;
      if (destroyed) return;
      if (full) render();
      else {
        if (crop) { renderCropControls(); fitLayoutSelection(); if (visible() && activeView === 'room' && imageReady) draw(canvas, state); }
        if (refs && visible()) renderReferencePositions();
      }
    }
    function writeEntryDimensions() {
      if (!entryWidth || !entryHeight || !entryUnit) return;
      entryDraft.widthCm = state.widthCm; entryDraft.heightCm = state.heightCm; entryDraft.unit = state.unit;
      entryWidth.value = String(rounded(fromCm(state.widthCm, state.unit)));
      entryHeight.value = String(rounded(fromCm(state.heightCm, state.unit)));
      entryUnit.value = state.unit;
      updateEntryMaximum();
      [entryWidth, entryHeight].forEach(field => field.setAttribute('aria-invalid', 'false'));
      if (entryError) entryError.hidden = true;
    }
    function updateEntryMaximum() {
      if (!entryWidth || !entryHeight) return;
      const divisor = entryDraft.unit === 'in' ? 2.54 : 1;
      entryWidth.max = String(1500 / divisor); entryHeight.max = String(rounded(MAX_HEIGHT_CM / divisor, 2));
    }
    function renderSummary() {
      if (!entrySummary || !confirmed || summaryDirty) return;
      const quote = exampleQuote(confirmed);
      entryFind('summary-dimensions').textContent = `${formatted(fromCm(confirmed.widthCm, confirmed.unit))} × ${formatted(fromCm(confirmed.heightCm, confirmed.unit))} ${confirmed.unit}`;
      entryFind('summary-material').textContent = MATERIALS[confirmed.material].name;
      entryFind('summary-total').textContent = `${formatted(quote.total)} ${CURRENCY}`;
      const panels = entryFind('summary-panels');
      if (panels) panels.textContent = panelDescription(confirmed);
      const extension = entryFind('summary-extension'), selectedExtension = designExtension(confirmed, artwork.naturalWidth, artwork.naturalHeight);
      if (extension) extension.textContent = selectedExtension.percent === 0 ? translate('originalProportions') : translate(selectedExtension.axis === 'horizontal' ? 'wider' : 'taller').replace('{percent}', formatted(selectedExtension.percent, Number.isInteger(selectedExtension.percent) ? 0 : 2));
      entrySummary.hidden = false;
      updateEntryStep();
    }
    function restoreConfirmedSelection(selection, draftOnly = false) {
      if (!imageReady || !selection?.configuration) return false;
      const config = selection.configuration, crop = config.crop;
      const materialId = typeof config.material === 'string' ? config.material : config.material?.id;
      if (config.schemaVersion !== 1 || config.productHandle !== section.dataset.productHandle || config.artwork?.reference !== section.dataset.artworkReference || config.artwork?.sha256 !== section.dataset.artworkSha256 || !MATERIALS[materialId] || !validCm(config.wall?.widthCm) || !validH(config.wall?.heightCm)) return false;
      if (!crop || !['x', 'y', 'width', 'height'].every(key => Number.isFinite(crop[key])) || crop.x < 0 || crop.y < 0 || crop.width <= 0 || crop.height <= 0 || crop.x + crop.width > 1.00000001 || crop.y + crop.height > 1.00000001) return false;
      const percent = config.designExtension?.percent;
      if (!Number.isFinite(percent) || percent < 0 || percent > 10) return false;
      const candidate = {...state, widthCm: config.wall.widthCm, heightCm: config.wall.heightCm, material: materialId, mirror: mirrorAllowed && Boolean(config.mirror), designExtension: {axis: config.designExtension.axis, percent}, zoom: 1};
      const extension = designExtension(candidate, artwork.naturalWidth, artwork.naturalHeight);
      if (extension.axis !== config.designExtension.axis) return false;
      const base = cropGeometry(candidate, artwork.naturalWidth, artwork.naturalHeight);
      candidate.zoom = base.normalized.width / crop.width;
      if (candidate.zoom < 1 - 1e-8 || candidate.zoom > 2 + 1e-8) return false;
      candidate.zoom = clamp(candidate.zoom, 1, 2);
      candidate.panX = crop.width < 1 - 1e-8 ? crop.x / (1 - crop.width) : .5;
      candidate.panY = crop.height < 1 - 1e-8 ? crop.y / (1 - crop.height) : .5;
      const reconstructed = cropGeometry(candidate, artwork.naturalWidth, artwork.naturalHeight).normalized;
      if (!['x', 'y', 'width', 'height'].every(key => Math.abs(reconstructed[key] - crop[key]) < 1e-7)) return false;
      Object.assign(state, candidate, {designExtension: extension, extensionEnabled: percent > 0});
      writeDimensionFields(); validateDraft(); writeEntryDimensions();
      // A saved crop that never reached the cart comes back as an editable draft:
      // the customer sees the same crop again but confirms it themselves.
      if (draftOnly) { summaryDirty = true; render(); updateEntryStep(); return true; }
      confirmed = snapshotState(); summaryDirty = false;
      const thumbnail = document.createElement('canvas');
      draw(thumbnail, confirmed, 640);
      const previewDataUrl = thumbnail.toDataURL('image/png');
      const image = entryFind('summary-image');
      if (image) { image.src = previewDataUrl; image.alt = translate('selectedCrop'); }
      const configuration = buildConfiguration(confirmed, {productHandle: section.dataset.productHandle, reference: section.dataset.artworkReference, sha256: section.dataset.artworkSha256, asset: section.dataset.artworkAsset, sourceWidth: artwork.naturalWidth, sourceHeight: artwork.naturalHeight});
      confirmedSelection = {configuration, previewDataUrl, quote: {...exampleQuote(confirmed), previewOnly: true}};
      if (entry) entry.dataset.selectionConfirmed = 'true';
      render(); renderSummary();
      return true;
    }
    function closeDialog() {
      if (purchaseBusy) return;
      if (!dialog?.open) return;
      flush();
      // Closing retains the last valid dimensions, crop and material without confirming a purchase selection.
      writeDimensionFields(); validateDraft(); writeEntryDimensions();
      drag = layoutDrag = referenceDrag = null;
      dialog.close();
      updateEntryStep();
      unlockScroll();
    }
    function openDialog(trigger) {
      if (!dialog || !entryWidth || !entryHeight) return;
      const fields = [[entryWidth, 'widthCm'], [entryHeight, 'heightCm']];
      const invalid = fields.filter(([field, key]) => field.value.trim() === '' || !validFor(key, entryDraft[key]));
      fields.forEach(([field]) => field.setAttribute('aria-invalid', String(invalid.some(([bad]) => bad === field))));
      if (entryError) entryError.hidden = invalid.length === 0;
      if (invalid.length) { invalid[0][0].focus(); return; }
      if (state.widthCm !== entryDraft.widthCm || state.heightCm !== entryDraft.heightCm || state.unit !== entryDraft.unit) dirty();
      state.widthCm = entryDraft.widthCm; state.heightCm = entryDraft.heightCm; state.unit = entryDraft.unit;
      writeDimensionFields(); validateDraft();
      opener = trigger || entryFind('continue');
      if (!dialog.open) { lockScroll(); dialog.showModal(); }
      updateEntryStep();
      schedule('full');
      const title = document.getElementById(dialog.getAttribute('aria-labelledby'));
      (title || closeButton)?.focus({preventScroll: true});
    }
    if (entry && entryWidth && entryHeight && entryUnit) {
      // A separate canonical draft avoids feeding rounded display values back into the wall size.
      [[entryWidth, 'widthCm'], [entryHeight, 'heightCm']].forEach(([field, key]) => {
        if (field.value.trim() !== '') entryDraft[key] = toCm(field.value, entryDraft.unit);
        field.addEventListener('input', () => {
          entryDraft[key] = field.value.trim() === '' ? null : toCm(field.value, entryDraft.unit);
          dirty();
          field.setAttribute('aria-invalid', String(!validFor(key, entryDraft[key])));
          if (entryError) entryError.hidden = validCm(entryDraft.widthCm) && validH(entryDraft.heightCm);
        }, options);
      });
      entryUnit.addEventListener('change', () => {
        if (!['cm', 'in'].includes(entryUnit.value)) { entryUnit.value = entryDraft.unit; return; }
        entryDraft.unit = entryUnit.value;
        [[entryWidth, 'widthCm'], [entryHeight, 'heightCm']].forEach(([field, key]) => {
          if (validFor(key, entryDraft[key])) field.value = String(rounded(fromCm(entryDraft[key], entryDraft.unit)));
        });
        updateEntryMaximum(); dirty();
      }, options);
      entry.addEventListener('submit', event => { event.preventDefault(); openDialog(event.submitter || entryFind('continue')); }, options);
      entryFind('edit')?.addEventListener('click', event => { event.preventDefault(); openDialog(event.currentTarget); }, options);
      updateEntryMaximum();
    }
    if (dialog) {
      closeButton?.addEventListener('click', closeDialog, options);
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(); }, options);
      let backdropDown = false;
      const outsideDialog = event => {
        if (event.target !== dialog) return false;
        const bounds = dialog.getBoundingClientRect();
        return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
      };
      dialog.addEventListener('pointerdown', event => { backdropDown = outsideDialog(event); }, options);
      dialog.addEventListener('click', event => { if (backdropDown && outsideDialog(event)) closeDialog(); backdropDown = false; }, options);
      dialog.addEventListener('close', () => { if (!dialog.open) { writeEntryDimensions(); updateEntryStep(); unlockScroll(); } }, options);
      entry?.addEventListener('marsela:purchase-state', event => {
        const detail = event.detail || {};
        purchasePhase = detail.phase || 'idle'; purchaseBusy = Boolean(detail.busy);
        if (editingLocked()) drag = layoutDrag = referenceDrag = null;
        if (confirmButton) { confirmButton.textContent = detail.buttonLabel || translate('confirmAdd'); confirmButton.disabled = purchaseBusy || !valid || !imageReady; }
        if (purchaseStatus) { purchaseStatus.textContent = detail.statusLabel || ''; purchaseStatus.hidden = !detail.statusLabel; }
        if (verifiedPrice) { verifiedPrice.textContent = detail.verifiedPrice || ''; verifiedPrice.hidden = !detail.verifiedPrice; }
        if (closeButton) closeButton.disabled = purchaseBusy;
        dialog?.setAttribute('aria-busy', String(purchaseBusy));
        lockPurchaseControls();
      }, options);
      confirmButton?.addEventListener('click', () => {
        if (purchaseBusy) return;
        if (!validateDraft()) { (width.getAttribute('aria-invalid') === 'true' ? width : height).focus(); return; }
        if (!imageReady) return;
        flush();
        try {
          const thumbnail = document.createElement('canvas');
          draw(thumbnail, state, 640);
          const previewDataUrl = thumbnail.toDataURL('image/png');
          const image = entryFind('summary-image');
          if (image) { image.src = previewDataUrl; image.alt = translate('selectedCrop'); }
          confirmed = snapshotState(); summaryDirty = false;
          const configuration = buildConfiguration(confirmed, {productHandle: section.dataset.productHandle, reference: section.dataset.artworkReference, sha256: section.dataset.artworkSha256, asset: section.dataset.artworkAsset, sourceWidth: artwork.naturalWidth, sourceHeight: artwork.naturalHeight});
          confirmedSelection = {configuration, previewDataUrl, quote: {...exampleQuote(confirmed), previewOnly: true}};
          if (entry) entry.dataset.selectionConfirmed = 'true';
          renderSummary();
          entry?.dispatchEvent(new CustomEvent('marsela:selection-confirmed', {bubbles: true, detail: {...JSON.parse(JSON.stringify(confirmedSelection)), purchaseRequested: true}}));
        } catch (_) { saveStatus.textContent = translate('saveFailed'); }
      }, options);
    }

    function panelDescription(snapshot) {
      const plan = panelPlan(snapshot.widthCm);
      return plural('panels', plan.count).replace('{count}', formatted(plan.count, 0)).replace('{width}', formatted(fromCm(plan.panelWidthCm, snapshot.unit), 2)).replace('{unit}', snapshot.unit);
    }
    function drawPanelLines(container, pixelWidth = 0) {
      if (!container) return;
      container.hidden = !guides?.checked || !imageReady;
      const plan = panelPlan(state.widthCm);
      if (container.dataset.panelCount !== String(plan.count)) {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < plan.count; i++) {
          if (i) {
            const line = document.createElement('i');
            line.className = 'ms-customizer__panel-line'; line.style.left = `${i / plan.count * 100}%`;
            fragment.appendChild(line);
          }
          const number = document.createElement('span');
          number.className = 'ms-customizer__panel-number'; number.style.left = `${(i + .5) / plan.count * 100}%`; number.textContent = String(i + 1);
          fragment.appendChild(number);
        }
        container.replaceChildren(fragment); container.dataset.panelCount = String(plan.count);
      }
      container.querySelectorAll('.ms-customizer__panel-number').forEach(number => { number.hidden = pixelWidth / plan.count < 24; });
    }
    function fitLayoutSelection() {
      if (!layoutBounds || !cropWindow || !imageReady) return;
      const crop = cropGeometry(state, artwork.naturalWidth, artwork.naturalHeight).normalized;
      const left = (state.mirror ? 1 - crop.x - crop.width : crop.x) * layoutBounds.width;
      Object.assign(cropWindow.style, {left: `${left}px`, top: `${crop.y * layoutBounds.height}px`, width: `${crop.width * layoutBounds.width}px`, height: `${crop.height * layoutBounds.height}px`});
      cropWindow.disabled = !valid || !imageReady || editingLocked();
      layoutImage.style.transform = state.mirror ? 'scaleX(-1)' : 'none';
      drawPanelLines(layoutPanels, crop.width * layoutBounds.width);
    }
    function fitLayout() {
      if (!visible() || activeView !== 'layout' || !layoutStage || !layoutArt || !imageReady || layoutStage.clientWidth <= 0 || layoutStage.clientHeight <= 0) return;
      const styles = getComputedStyle(layoutStage);
      const pad = Object.fromEntries(['left', 'right', 'top', 'bottom'].map(side => [side, parseFloat(styles[`padding${side[0].toUpperCase()}${side.slice(1)}`]) || 0]));
      const availableWidth = Math.max(1, layoutStage.clientWidth - pad.left - pad.right), availableHeight = Math.max(1, layoutStage.clientHeight - pad.top - pad.bottom);
      const extension = designScale(state, artwork.naturalWidth, artwork.naturalHeight);
      const designWidth = artwork.naturalWidth * extension.x, designHeight = artwork.naturalHeight * extension.y;
      const scale = Math.min(availableWidth / designWidth, availableHeight / designHeight);
      layoutBounds = {width: designWidth * scale, height: designHeight * scale};
      Object.assign(layoutArt.style, {left: `${pad.left + (availableWidth - layoutBounds.width) / 2}px`, top: `${pad.top + (availableHeight - layoutBounds.height) / 2}px`, width: `${layoutBounds.width}px`, height: `${layoutBounds.height}px`});
      if (find('layout-width')) find('layout-width').textContent = `${formatted(fromCm(state.widthCm, state.unit), 1)} ${state.unit}`;
      if (find('layout-height')) find('layout-height').textContent = `${formatted(fromCm(state.heightCm, state.unit), 1)} ${state.unit}`;
      fitLayoutSelection();
    }
    viewButtons.forEach(button => button.addEventListener('click', () => {
      const next = button.dataset.msCustomizerView;
      if (!['layout', 'room'].includes(next)) return;
      activeView = next; drag = layoutDrag = referenceDrag = null;
      viewButtons.forEach(candidate => candidate.setAttribute('aria-pressed', String(candidate.dataset.msCustomizerView === activeView)));
      if (layoutView) layoutView.hidden = activeView !== 'layout';
      if (roomView) roomView.hidden = activeView !== 'room';
      schedule();
    }, options));
    cropWindow?.addEventListener('pointerdown', event => {
      if (editingLocked() || !valid || !imageReady || !layoutBounds || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const crop = cropGeometry(state, artwork.naturalWidth, artwork.naturalHeight).normalized;
      layoutDrag = {id: event.pointerId, x: event.clientX, y: event.clientY, panX: state.panX, panY: state.panY, excessX: layoutBounds.width * (1 - crop.width), excessY: layoutBounds.height * (1 - crop.height), direction: state.mirror ? -1 : 1};
      cropWindow.setPointerCapture(event.pointerId); event.preventDefault();
    }, options);
    cropWindow?.addEventListener('pointermove', event => {
      if (!layoutDrag || layoutDrag.id !== event.pointerId || !valid) return;
      if (layoutDrag.excessX > .01) state.panX = clamp(layoutDrag.panX + (event.clientX - layoutDrag.x) / layoutDrag.excessX * layoutDrag.direction, 0, 1);
      if (layoutDrag.excessY > .01) state.panY = clamp(layoutDrag.panY + (event.clientY - layoutDrag.y) / layoutDrag.excessY, 0, 1);
      dirty(); schedule('crop');
    }, options);
    const endLayoutDrag = event => { if (layoutDrag?.id === event.pointerId) { layoutDrag = null; flush(); } };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => cropWindow?.addEventListener(name, endLayoutDrag, options));
    cropWindow?.addEventListener('keydown', event => {
      if (editingLocked() || !valid || !imageReady || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      const amount = event.shiftKey ? .1 : .02;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') state.panX = clamp(state.panX + (event.key === 'ArrowRight' ? 1 : -1) * (state.mirror ? -1 : 1) * amount, 0, 1);
      else state.panY = clamp(state.panY + (event.key === 'ArrowDown' ? 1 : -1) * amount, 0, 1);
      dirty(); schedule('crop'); event.preventDefault();
    }, options);

    function displayDimensions() {
      return `${formatted(fromCm(state.widthCm, state.unit))} × ${formatted(fromCm(state.heightCm, state.unit))} ${state.unit}`;
    }
    function writeDimensionFields() {
      // Display rounding never flows back into canonical centimetres during a unit switch.
      width.value = String(rounded(fromCm(state.widthCm, state.unit)));
      height.value = String(rounded(fromCm(state.heightCm, state.unit)));
      const divisor = state.unit === 'in' ? 2.54 : 1;
      width.max = String(1500 / divisor); height.max = String(rounded(MAX_HEIGHT_CM / divisor, 2));
      unit.value = state.unit;
    }
    function validateDraft() {
      const widthValid = width.value.trim() !== '' && validCm(toCm(width.value, state.unit));
      const heightValid = height.value.trim() !== '' && validH(toCm(height.value, state.unit));
      width.setAttribute('aria-invalid', String(!widthValid));
      height.setAttribute('aria-invalid', String(!heightValid));
      valid = widthValid && heightValid;
      error.hidden = valid;
      section.dataset.msCustomizerValid = String(valid);
      return valid;
    }
    function stagePadding(multiplier = 1) {
      const styles = getComputedStyle(stage);
      return Object.fromEntries(['left', 'right', 'top', 'bottom'].map(side => [side, (parseFloat(styles[`padding${side[0].toUpperCase()}${side.slice(1)}`]) || 0) * multiplier]));
    }
    function announceReference(type, action) {
      if (!referenceStatus) return;
      lastReferenceAnnouncement = {type, action};
      if (action === 'cleared') { referenceStatus.textContent = translate('referencesCleared'); return; }
      const name = translate(type);
      const phrase = action === 'added' ? 'referenceAdded' : action === 'removed' ? 'referenceRemoved' : 'referenceMoved';
      referenceStatus.textContent = translate(phrase).replace('{name}', name);
    }
    function renderReferencePositions() {
      if (!scene) return;
      Object.keys(REFERENCES).forEach(type => {
        const node = referenceNodes[type];
        if (!node || !state.references[type].visible) return;
        const bounds = referenceGeometry(type, state.references[type], scene);
        Object.assign(node.style, {left: `${bounds.left}px`, top: `${bounds.top}px`, bottom: 'auto', width: `${bounds.width}px`, height: `${bounds.height}px`});
      });
    }
    function renderReferences() {
      if (!scene) return;
      Object.entries(REFERENCES).forEach(([type, definition]) => {
        const reference = state.references[type], node = referenceNodes[type], button = referenceButtons[type];
        if (button) {
          button.setAttribute('aria-pressed', String(reference.visible));
          button.disabled = !valid || !imageReady;
          const label = button.querySelector('[data-ms-customizer-add-label]') || button;
          label.textContent = translate(`${reference.visible ? 'remove' : 'add'}${type[0].toUpperCase()}${type.slice(1)}`);
        }
        if (!node) return;
        node.hidden = !reference.visible || !imageReady;
        node.disabled = !valid || !imageReady;
        const bounds = referenceGeometry(type, reference, scene);
        // Clamp only a render position; preserve the user's chosen center through narrower layouts.
        Object.assign(node.style, {left: `${bounds.left}px`, top: `${bounds.top}px`, bottom: 'auto', width: `${bounds.width}px`, height: `${bounds.height}px`});
        const sizeLabel = node.querySelector('span');
        if (sizeLabel) {
          const heightLabel = formatted(fromCm(definition.heightCm, state.unit), state.unit === 'in' ? 1 : 0);
          sizeLabel.textContent = type === 'crib' ? `${formatted(fromCm(definition.widthCm, state.unit), state.unit === 'in' ? 1 : 0)} × ${heightLabel} ${state.unit}` : `${heightLabel} ${state.unit}`;
        }
        node.setAttribute('aria-label', translate('referenceLabel').replace('{name}', translate(type)).replace('{height}', formatted(definition.heightCm, 0)));
      });
      if (clearReferences) clearReferences.disabled = !Object.values(state.references).some(reference => reference.visible);
    }
    function fitCanvas() {
      if (!visible() || stage.clientWidth <= 0 || stage.clientHeight <= 0) return;
      if (wall) {
        scene = sceneGeometry(state, stage.clientWidth, stage.clientHeight, stagePadding());
        Object.assign(wall.style, {left: `${scene.wallLeft}px`, top: `${scene.wallTop}px`, width: `${scene.wallWidth}px`, height: `${scene.wallHeight}px`});
        canvas.style.width = '100%'; canvas.style.height = '100%';
        stage.style.setProperty('--ms-scene-floor-bottom', `${stage.clientHeight - scene.floor}px`);
        stage.style.setProperty('--ms-scene-wall-left', `${scene.wallLeft}px`);
        stage.style.setProperty('--ms-scene-wall-right', `${stage.clientWidth - scene.wallLeft - scene.wallWidth}px`);
        stage.style.setProperty('--ms-scene-wall-top', `${scene.wallTop}px`);
        wall.classList.toggle('ms-customizer__wall--guides', Boolean(guides?.checked));
        renderReferences();
        drawPanelLines(panelLines, scene.wallWidth);
      } else {
        const padding = stagePadding();
        const scale = Math.min((stage.clientWidth - padding.left - padding.right) / state.widthCm, (stage.clientHeight - padding.top - padding.bottom) / state.heightCm);
        canvas.style.width = `${state.widthCm * scale}px`;
        canvas.style.height = `${state.heightCm * scale}px`;
      }
      if (find('wall-width')) find('wall-width').textContent = `${formatted(fromCm(state.widthCm, state.unit), 1)} ${state.unit}`;
      if (find('wall-height')) find('wall-height').textContent = `${formatted(fromCm(state.heightCm, state.unit), 1)} ${state.unit}`;
    }
    function draw(target, snapshot, longestEdge = 1200) {
      const crop = cropGeometry(snapshot, artwork.naturalWidth, artwork.naturalHeight);
      if (!crop) return null;
      const scale = longestEdge / Math.max(snapshot.widthCm, snapshot.heightCm);
      const pixelWidth = Math.max(1, Math.round(snapshot.widthCm * scale));
      const pixelHeight = Math.max(1, Math.round(snapshot.heightCm * scale));
      // Only reset the backing store when dimensions change.
      if (target.width !== pixelWidth) target.width = pixelWidth;
      if (target.height !== pixelHeight) target.height = pixelHeight;
      const targetContext = target.getContext('2d');
      if (!targetContext) throw new Error('Canvas unavailable');
      targetContext.clearRect(0, 0, pixelWidth, pixelHeight);
      targetContext.save();
      if (snapshot.mirror) { targetContext.translate(pixelWidth, 0); targetContext.scale(-1, 1); }
      targetContext.drawImage(artwork, crop.x, crop.y, crop.width, crop.height, 0, 0, pixelWidth, pixelHeight);
      targetContext.restore();
      watermarkPreview(targetContext, pixelWidth, pixelHeight);
      return crop;
    }
    function renderCropControls() {
      zoom.value = String(state.zoom); panX.value = String(Math.round(state.panX * 100)); panY.value = String(Math.round(state.panY * 100)); mirror.checked = state.mirror;
      find('zoom-output').textContent = `${formatted(state.zoom)}×`;
      find('x-output').textContent = `${Math.round(state.panX * 100)}%`;
      find('y-output').textContent = `${Math.round(state.panY * 100)}%`;
      zoom.setAttribute('aria-valuetext', translate('zoomValue').replace('{value}', formatted(state.zoom)));
      panX.setAttribute('aria-valuetext', `${Math.round(state.panX * 100)}%`);
      panY.setAttribute('aria-valuetext', `${Math.round(state.panY * 100)}%`);
      const usable = valid && imageReady;
      state.designExtension = designExtension(state, artwork.naturalWidth, artwork.naturalHeight);
      if (extend) { extend.disabled = !usable; extend.setAttribute('aria-pressed', String(state.extensionEnabled)); extend.setAttribute('aria-expanded', String(state.extensionEnabled)); }
      if (extensionControls) extensionControls.hidden = !state.extensionEnabled;
      const signed = signedStretch(state.designExtension);
      const stretchText = value => value === 0 ? translate('originalProportions') : translate(value > 0 ? 'wider' : 'taller').replace('{percent}', formatted(Math.abs(value), 0));
      if (extensionRange) { extensionRange.value = String(signed); extensionRange.disabled = !usable || !state.extensionEnabled; extensionRange.setAttribute('aria-valuetext', stretchText(signed)); }
      if (extensionOutput) extensionOutput.textContent = `${signed > 0 ? '+' : signed < 0 ? '−' : ''}${Math.abs(signed)}%`;
      if (extensionMin) extensionMin.textContent = stretchText(-10);
      if (extensionMax) extensionMax.textContent = stretchText(10);
      const crop = imageReady ? cropGeometry(state, artwork.naturalWidth, artwork.naturalHeight) : null;
      panX.disabled = !usable || !crop || artwork.naturalWidth - crop.width < .01;
      panY.disabled = !usable || !crop || artwork.naturalHeight - crop.height < .01;
    }
    function render() {
      if (destroyed) return;
      const quote = exampleQuote(state);
      find('dimensions').textContent = displayDimensions();
      find('area').textContent = valid ? `${formatted(quote.areaM2)} m²` : '—';
      const minimumNote = find('minimum');
      if (minimumNote) { minimumNote.hidden = !(valid && quote.minimumApplied); minimumNote.textContent = translate('minimum').replaceAll('{area}', formatted(MIN_BILLABLE_M2, 0)); }
      find('rate').textContent = `${formatted(quote.unitRate)} ${CURRENCY} / m²`;
      find('total').textContent = valid ? `${formatted(quote.total)} ${CURRENCY}` : '—';
      const live = find('live');
      if (live) {
        live.textContent = valid
          ? `${formatted(quote.areaM2)} m² · ${MATERIALS[state.material].name} · ${formatted(quote.total)} ${CURRENCY}${quote.minimumApplied ? ` (${translate('pricedAs').replace('{area}', formatted(MIN_BILLABLE_M2, 0))})` : ''}`
          : translate('enterSize');
      }
      const plan = panelPlan(state.widthCm);
      if (find('panel-count')) find('panel-count').textContent = valid ? String(plan.count) : '—';
      if (find('panel-width')) find('panel-width').textContent = valid ? `${formatted(fromCm(plan.panelWidthCm, state.unit))} ${state.unit}` : '—';
      renderCropControls();
      const usable = valid && imageReady;
      [zoom, mirror, reset].forEach(control => { control.disabled = !usable || (control === mirror && !mirrorAllowed); });
      if (confirmButton) confirmButton.disabled = !usable || purchaseBusy;
      if (guides) guides.disabled = !usable;
      material.value = state.material;
      materialOptions.forEach(option => { option.checked = option.value === state.material; });
      const crop = imageReady ? cropGeometry(state, artwork.naturalWidth, artwork.naturalHeight) : null;
      panX.disabled = !usable || !crop || artwork.naturalWidth - crop.width < .01;
      panY.disabled = !usable || !crop || artwork.naturalHeight - crop.height < .01;
      canvas.hidden = !imageReady;
      if (layoutImage) layoutImage.hidden = !imageReady;
      if (cropWindow) { cropWindow.hidden = !imageReady; cropWindow.disabled = !usable; }
      imageStatus.hidden = imageReady;
      if (!imageReady) imageStatus.textContent = translate(imageFailed ? 'artworkUnavailable' : 'loadingArtwork');
      fitCanvas(); fitLayout();
      if (visible() && activeView === 'room' && imageReady) draw(canvas, state);
      lockPurchaseControls();
    }

    [width, height].forEach((field, index) => field.addEventListener('input', () => {
      const candidate = toCm(field.value, state.unit);
      // Editing one field must not round the unchanged dimension in the other field.
      if (field.value.trim() !== '' && (index ? validH(candidate) : validCm(candidate))) state[index ? 'heightCm' : 'widthCm'] = candidate;
      validateDraft(); dirty(); schedule();
    }, options));
    unit.addEventListener('change', () => {
      if (!valid || !['cm', 'in'].includes(unit.value)) { unit.value = state.unit; return; }
      state.unit = unit.value; writeDimensionFields(); dirty(); schedule();
    }, options);
    material.addEventListener('change', () => { if (MATERIALS[material.value]) { state.material = material.value; dirty(); schedule(); } }, options);
    materialOptions.forEach(option => option.addEventListener('change', () => { if (option.checked && MATERIALS[option.value]) { state.material = option.value; dirty(); schedule(); } }, options));
    guides?.addEventListener('change', () => { fitCanvas(); fitLayoutSelection(); }, options);
    zoom.addEventListener('input', () => { state.zoom = clamp(Number(zoom.value) || 1, 1, 2); dirty(); schedule('crop'); }, options);
    panX.addEventListener('input', () => { state.panX = clamp(Number(panX.value) / 100, 0, 1); dirty(); schedule('crop'); }, options);
    panY.addEventListener('input', () => { state.panY = clamp(Number(panY.value) / 100, 0, 1); dirty(); schedule('crop'); }, options);
    mirror.addEventListener('change', () => { state.mirror = mirrorAllowed && mirror.checked; dirty(); schedule('crop'); }, options);
    extend?.addEventListener('click', () => {
      if (!valid || !imageReady) return;
      state.extensionEnabled = !state.extensionEnabled;
      if (!state.extensionEnabled) state.designExtension = {axis: 'none', percent: 0};
      dirty(); schedule();
      // Show the slider at once: render now, then bring it into view and focus it.
      flush();
      if (state.extensionEnabled && extensionControls) {
        const adjustments = extensionControls.closest('details');
        if (adjustments && !adjustments.open) adjustments.open = true;
        requestAnimationFrame(() => {
          extensionControls.scrollIntoView({block: 'nearest', behavior: 'smooth'});
          extensionRange?.focus({preventScroll: true});
        });
      }
    }, options);
    extensionRange?.addEventListener('input', () => {
      if (!state.extensionEnabled || !valid || !imageReady) return;
      state.designExtension = designExtension({...state, designExtension: {signed: clamp(Math.round(Number(extensionRange.value)) || 0, -10, 10)}}, artwork.naturalWidth, artwork.naturalHeight);
      dirty(); schedule();
    }, options);
    reset.addEventListener('click', () => { state.zoom = 1; state.panX = state.panY = .5; state.mirror = false; state.designExtension = {axis: 'none', percent: 0}; state.extensionEnabled = false; dirty(); schedule(); }, options);
    canvas.addEventListener('pointerdown', event => {
      if (editingLocked() || !valid || !imageReady || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const crop = cropGeometry(state, artwork.naturalWidth, artwork.naturalHeight);
      const bounds = canvas.getBoundingClientRect();
      drag = {id: event.pointerId, x: event.clientX, y: event.clientY, panX: state.panX, panY: state.panY, excessX: bounds.width * (artwork.naturalWidth / crop.width - 1), excessY: bounds.height * (artwork.naturalHeight / crop.height - 1), direction: state.mirror ? -1 : 1};
      canvas.setPointerCapture(event.pointerId);
      event.preventDefault();
    }, options);
    canvas.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId || !valid) return;
      if (drag.excessX > .01) state.panX = clamp(drag.panX - (event.clientX - drag.x) / drag.excessX * drag.direction, 0, 1);
      if (drag.excessY > .01) state.panY = clamp(drag.panY - (event.clientY - drag.y) / drag.excessY, 0, 1);
      dirty(); schedule('crop');
    }, options);
    const endDrag = event => { if (drag?.id === event.pointerId) { drag = null; flush(); } };
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => canvas.addEventListener(name, endDrag, options));

    Object.keys(REFERENCES).forEach(type => {
      const node = referenceNodes[type], button = referenceButtons[type];
      button?.addEventListener('click', () => {
        if (editingLocked() || !valid || !imageReady) return;
        state.references[type].visible = !state.references[type].visible;
        renderReferences(); announceReference(type, state.references[type].visible ? 'added' : 'removed');
      }, options);
      if (!node) return;
      node.addEventListener('pointerdown', event => {
        if (editingLocked() || !valid || !imageReady || !scene || (event.pointerType === 'mouse' && event.button !== 0)) return;
        const bounds = referenceGeometry(type, state.references[type], scene);
        referenceDrag = {type, id: event.pointerId, clientX: event.clientX, center: bounds.x, interiorWidth: scene.interiorWidth};
        node.setPointerCapture(event.pointerId); event.preventDefault();
      }, options);
      node.addEventListener('pointermove', event => {
        if (!referenceDrag || referenceDrag.type !== type || referenceDrag.id !== event.pointerId || !valid || !scene) return;
        const candidate = {x: referenceDrag.center + (event.clientX - referenceDrag.clientX) / referenceDrag.interiorWidth};
        state.references[type].x = referenceGeometry(type, candidate, scene).x;
        schedule('references');
      }, options);
      const endReferenceDrag = event => {
        if (referenceDrag?.type !== type || referenceDrag.id !== event.pointerId) return;
        referenceDrag = null; flush(); announceReference(type, 'moved');
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(name => node.addEventListener(name, endReferenceDrag, options));
      node.addEventListener('keydown', event => {
        if (editingLocked() || !valid || !imageReady || !scene || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        const current = referenceGeometry(type, state.references[type], scene).x;
        const candidate = {x: current + direction * (event.shiftKey ? 10 : 2) * scene.scale / scene.interiorWidth};
        state.references[type].x = referenceGeometry(type, candidate, scene).x;
        renderReferences(); announceReference(type, 'moved'); event.preventDefault();
      }, options);
    });
    clearReferences?.addEventListener('click', () => {
      if (editingLocked()) return;
      Object.values(state.references).forEach(reference => { reference.visible = false; });
      referenceDrag = null; renderReferences();
      announceReference(null, 'cleared');
    }, options);

    artwork.addEventListener('load', () => { if (destroyed) return; imageReady = artwork.naturalWidth > 0 && artwork.naturalHeight > 0; imageFailed = !imageReady; render(); if (imageReady) entry?.dispatchEvent(new CustomEvent('marsela:customizer-ready', {bubbles: true})); }, options);
    artwork.addEventListener('error', () => { if (destroyed) return; imageReady = false; imageFailed = true; render(); }, options);
    let observer;
    if ('ResizeObserver' in window) { observer = new ResizeObserver(() => { if (!destroyed && imageReady && visible()) schedule(); }); observer.observe(stage); if (layoutStage) observer.observe(layoutStage); }
    else window.addEventListener('resize', () => { if (visible()) schedule(); }, options);
    instances.set(section, {entry, getConfirmedSelection: () => confirmedSelection && !summaryDirty ? JSON.parse(JSON.stringify(confirmedSelection)) : null, restoreConfirmedSelection, refresh: () => { render(); renderSummary(); if (lastReferenceAnnouncement) announceReference(lastReferenceAnnouncement.type, lastReferenceAnnouncement.action); }, destroy: () => {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      if (dialog?.open) dialog.close();
      unlockScroll(false);
      controller.abort(); observer?.disconnect();
    }});
    writeDimensionFields(); validateDraft(); render(); updateEntryStep();
    if (layoutImage) layoutImage.src = section.dataset.artworkSrc;
    artwork.src = section.dataset.artworkSrc;
  }
  function initialiseWithin(root) {
    if (root.matches?.('[data-ms-customizer]')) initialise(root);
    root.querySelectorAll('[data-ms-customizer]').forEach(initialise);
  }
  window.MarselaCustomizer = {initialiseWithin, getConfirmedSelection: entry => {
    for (const instance of instances.values()) if (instance.entry === entry) return instance.getConfirmedSelection();
    return null;
  }, restoreConfirmedSelection: (entry, selection) => {
    for (const instance of instances.values()) if (instance.entry === entry) return instance.restoreConfirmedSelection(selection);
    return false;
  }, restoreDraft: (entry, selection) => {
    for (const instance of instances.values()) if (instance.entry === entry) return instance.restoreConfirmedSelection(selection, true);
    return false;
  }};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseWithin(document), {once: true});
  else initialiseWithin(document);
  document.addEventListener('shopify:section:load', event => initialiseWithin(event.target));
  document.addEventListener('shopify:section:unload', event => instances.forEach((instance, section) => {
    if (event.target === section || event.target.contains(section)) { instance.destroy(); instances.delete(section); }
  }));
})();
